import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Activity, Zap, Shield, Clock, FileText, Users, Star, ArrowRight, Check, MessageCircle, Phone, ChevronRight, Upload, Brain, Award, Stethoscope, Smartphone, Sparkles, Eye, Search, Bone, HeartPulse, CircleDot } from 'lucide-react';

const WHATSAPP_LINK = 'https://wa.me/919028776813?text=Hi%2C%20I%20want%20to%20try%20ChestBuddy%20for%20my%20clinic';
const PROMO_CODE = 'SAVE75';
const PROMO_DEADLINE = 'limited time';

const ROTATING_TAGLINES = [
  'Stop second-guessing X-rays at 9 PM.',
  'AI second opinion — in 10 seconds, on your phone.',
  'Chest, Extremity & Abdominal X-rays. 25+ conditions detected.',
  'Pneumonia, TB, effusion, fractures, dislocations, obstruction — in one read.',
  'Costs less than a cup of filter coffee. Saves you hours.',
  'No radiologist queue. No callbacks. Just clarity.',
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [taglineIdx, setTaglineIdx] = useState(0);

  // Preserve ?coupon= param from WhatsApp links through the funnel
  // Fallback: if no coupon in URL, use the active launch promo
  const couponFromUrl = (typeof window !== 'undefined')
    ? new URLSearchParams(window.location.search).get('coupon')
    : null;
  const activeCoupon = couponFromUrl || PROMO_CODE;
  const registerHref = `/login?mode=register&coupon=${encodeURIComponent(activeCoupon)}`;
  const loginHref = `/login${couponFromUrl ? `?coupon=${encodeURIComponent(couponFromUrl)}` : ''}`;

  useEffect(() => {
    const id = setInterval(() => {
      setTaglineIdx((i) => (i + 1) % ROTATING_TAGLINES.length);
    }, 3200);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      {/* Promo banner - drives conversion */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white" data-testid="promo-banner">
        <button
          onClick={() => navigate(registerHref)}
          className="w-full max-w-6xl mx-auto px-4 py-2.5 flex items-center justify-center gap-2 text-sm font-medium hover:bg-black/5 transition-colors"
          data-testid="promo-banner-btn"
        >
          <Sparkles className="w-4 h-4" />
          <span>
            <strong>75% OFF</strong> launch promo — Pro plan <s className="opacity-60 mr-1">₹5,500</s>
            <strong className="text-base"> ₹1,375 / 6 months</strong> &middot; auto-applied
          </span>
          <ArrowRight className="w-4 h-4 ml-1" />
        </button>
      </div>

      {/* Top bar */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-stone-100">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="w-6 h-6 text-[#1A4D2E]" />
            <span className="font-heading font-semibold text-stone-900">ChestBuddy</span>
            <span className="text-[9px] font-semibold uppercase tracking-wider text-stone-400 hidden sm:block">GP-Assist</span>
          </div>
          <div className="flex items-center gap-3">
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="text-xs text-stone-500 hover:text-[#1A4D2E] hidden md:flex items-center gap-1">
              <Phone className="w-3 h-3" /> +91 90287 76813
            </a>
            <Button variant="ghost" size="sm" onClick={() => navigate(loginHref)} className="text-sm text-stone-600" data-testid="landing-login-btn">
              Sign In
            </Button>
            <Button size="sm" onClick={() => navigate(registerHref)} className="bg-[#1A4D2E] hover:bg-[#143B23] text-white text-sm gap-1" data-testid="landing-start-btn">
              Start Free <ArrowRight className="w-3 h-3" />
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#1A4D2E]/[0.03] to-transparent" />
        <div className="max-w-6xl mx-auto px-4 pt-16 pb-20 relative">
          <div className="max-w-3xl mx-auto text-center">
            <Badge className="bg-amber-500 text-white px-4 py-1 text-xs font-semibold mb-6" data-testid="hero-badge">
              2 Free X-rays — Chest, Extremity or Abdominal — No Credit Card Needed
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-medium tracking-tight text-stone-900 leading-[1.1]">
              Your AI radiology assistant<br />
              <span className="text-[#1A4D2E]">— in your pocket, at ₹99 a case.</span>
            </h1>
            <p className="text-base sm:text-lg text-stone-600 mt-6 max-w-2xl mx-auto leading-relaxed">
              Built for General Practitioners who see X-rays every day but don&apos;t always have a radiologist on call. Upload a <strong className="text-stone-900">Chest, Extremity or Abdominal</strong> X-ray — or just snap it with your phone — and in <strong className="text-stone-900">under 10 seconds</strong> you get a GP-friendly read, severity score, and a patient-ready PDF report.
            </p>

            {/* Three modules — top-of-fold visibility */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-8 max-w-3xl mx-auto text-left" data-testid="hero-modules">
              <div className="flex items-start gap-3 p-3 rounded-lg bg-white border-2 border-[#1A4D2E]/20">
                <div className="w-9 h-9 rounded-lg bg-[#1A4D2E] flex items-center justify-center flex-shrink-0">
                  <HeartPulse className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-sm font-heading font-semibold text-stone-900">Chest X-Ray</p>
                  <p className="text-xs text-stone-600 mt-0.5 leading-snug">Pneumonia, TB, effusion, cardiomegaly, COPD, pneumothorax + 6 more</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 rounded-lg bg-white border-2 border-amber-300 relative">
                <span className="absolute -top-2 -right-2"><Badge className="bg-amber-500 text-white text-[9px] px-1.5 py-0">NEW</Badge></span>
                <div className="w-9 h-9 rounded-lg bg-amber-500 flex items-center justify-center flex-shrink-0">
                  <Bone className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-sm font-heading font-semibold text-stone-900">Extremity X-Ray</p>
                  <p className="text-xs text-stone-600 mt-0.5 leading-snug">Fractures (all types), dislocations, arthritis, foreign bodies</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 rounded-lg bg-white border-2 border-blue-300 relative">
                <span className="absolute -top-2 -right-2"><Badge className="bg-amber-500 text-white text-[9px] px-1.5 py-0">NEW</Badge></span>
                <div className="w-9 h-9 rounded-lg bg-blue-500 flex items-center justify-center flex-shrink-0">
                  <CircleDot className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-sm font-heading font-semibold text-stone-900">Abdominal X-Ray</p>
                  <p className="text-xs text-stone-600 mt-0.5 leading-snug">Obstruction, free air, ileus, renal stones, urgency flag</p>
                </div>
              </div>
            </div>

            {/* Supporting feature bullets */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4 max-w-3xl mx-auto text-left">
              <div className="flex items-start gap-2.5 p-3 rounded-lg bg-stone-50 border border-stone-200">
                <Brain className="w-4 h-4 text-[#1A4D2E] mt-0.5 flex-shrink-0" />
                <p className="text-xs text-stone-700"><strong>Severity scoring</strong> in GP-friendly language</p>
              </div>
              <div className="flex items-start gap-2.5 p-3 rounded-lg bg-stone-50 border border-stone-200">
                <FileText className="w-4 h-4 text-[#1A4D2E] mt-0.5 flex-shrink-0" />
                <p className="text-xs text-stone-700"><strong>Professional PDF</strong> with medico-legal timestamp</p>
              </div>
              <div className="flex items-start gap-2.5 p-3 rounded-lg bg-stone-50 border border-stone-200">
                <Smartphone className="w-4 h-4 text-[#1A4D2E] mt-0.5 flex-shrink-0" />
                <p className="text-xs text-stone-700"><strong>Works anywhere</strong> — clinic, home, night duty</p>
              </div>
            </div>

            <p className="text-sm text-stone-500 mt-8 italic">
              No radiologist queue. No second-day follow-ups. No uncertainty.<br />
              Just a calm, always-available second opinion — <strong className="not-italic text-stone-900">costing less than a cup of filter coffee.</strong>
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8">
              <Button size="lg" onClick={() => navigate(registerHref)} className="bg-[#1A4D2E] hover:bg-[#143B23] text-white text-base px-8 h-12 gap-2 rounded-xl" data-testid="hero-cta">
                Start Free — 2 X-rays on the House <ArrowRight className="w-4 h-4" />
              </Button>
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-[#1A4D2E] font-medium hover:underline" data-testid="hero-whatsapp">
                <MessageCircle className="w-4 h-4" /> or WhatsApp us to get started
              </a>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-8 text-sm text-stone-500">
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-[#1A4D2E]" /> Instant results</span>
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-[#1A4D2E]" /> 25+ conditions detected</span>
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-[#1A4D2E]" /> PDF reports with disclaimer</span>
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-[#1A4D2E]" /> Works on phone</span>
            </div>
          </div>
        </div>
      </section>

      {/* Rotating taglines strip */}
      <section className="bg-[#1A4D2E] py-5 border-y border-[#1A4D2E]/20 overflow-hidden" data-testid="rotating-taglines">
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-center gap-3 text-center">
          <Zap className="w-5 h-5 text-amber-400 flex-shrink-0 animate-pulse" />
          <p key={taglineIdx} className="text-white font-heading text-base sm:text-lg font-medium animate-[fadeIn_400ms_ease-out] tracking-tight">
            {ROTATING_TAGLINES[taglineIdx]}
          </p>
        </div>
      </section>

      {/* What ChestBuddyAI actually does — positioning + trust */}
      <section className="py-16 bg-white" data-testid="positioning-section">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-10">
            <Badge className="bg-[#1A4D2E]/10 text-[#1A4D2E] mb-3">What it actually does</Badge>
            <h2 className="text-2xl sm:text-3xl font-heading font-medium text-stone-900">
              Early warnings <em className="text-[#1A4D2E] not-italic">and</em> evident detection — in one read
            </h2>
            <p className="text-stone-500 mt-3 max-w-2xl mx-auto text-sm sm:text-base">
              Every finding comes with a confidence score from 10% to 95%. You see both the subtle stuff a tired GP might overlook AND the clearly evident pathology — and you decide what to act on.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Early warning */}
            <Card className="border-amber-200 bg-amber-50/40 shadow-sm">
              <CardContent className="pt-5 pb-5">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-9 h-9 rounded-md bg-amber-500 flex items-center justify-center">
                    <Search className="w-4 h-4 text-white" />
                  </div>
                  <p className="text-[10px] font-semibold text-amber-700 uppercase tracking-wider">10-30% confidence</p>
                </div>
                <h3 className="font-heading font-semibold text-stone-900">Early Warning</h3>
                <p className="text-sm text-stone-600 mt-2 leading-relaxed">
                  Subtle findings — small effusions, faint infiltrates, borderline cardiomegaly, early apical TB lesions. Flagged so you can <strong>investigate further or follow up</strong>.
                </p>
              </CardContent>
            </Card>

            {/* Moderate */}
            <Card className="border-orange-200 bg-orange-50/40 shadow-sm">
              <CardContent className="pt-5 pb-5">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-9 h-9 rounded-md bg-orange-500 flex items-center justify-center">
                    <Eye className="w-4 h-4 text-white" />
                  </div>
                  <p className="text-[10px] font-semibold text-orange-700 uppercase tracking-wider">35-65% confidence</p>
                </div>
                <h3 className="font-heading font-semibold text-stone-900">Likely Finding</h3>
                <p className="text-sm text-stone-600 mt-2 leading-relaxed">
                  Probable pathology that warrants confirmation — repeat X-ray, CT, or labs. The AI explains <strong>what pattern it sees</strong>, not just that something is there.
                </p>
              </CardContent>
            </Card>

            {/* Evident */}
            <Card className="border-red-200 bg-red-50/40 shadow-sm">
              <CardContent className="pt-5 pb-5">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-9 h-9 rounded-md bg-red-600 flex items-center justify-center">
                    <Stethoscope className="w-4 h-4 text-white" />
                  </div>
                  <p className="text-[10px] font-semibold text-red-700 uppercase tracking-wider">70-95% confidence</p>
                </div>
                <h3 className="font-heading font-semibold text-stone-900">Evident Pathology</h3>
                <p className="text-sm text-stone-600 mt-2 leading-relaxed">
                  Classic, high-confidence findings — clear consolidation, large effusions, obvious cardiomegaly. <strong>Confirms what you already suspect</strong> + adds medico-legal documentation.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Honest disclosure (builds trust with skeptical doctors) */}
          <div className="mt-8 p-4 rounded-lg bg-stone-50 border border-stone-200 max-w-3xl mx-auto">
            <p className="text-xs text-stone-600 leading-relaxed">
              <strong className="text-stone-900">Honest note for doctors:</strong> ChestBuddyAI catches what&apos;s <em>visible on the chest X-ray</em> — subtle, moderate or evident. It does NOT predict disease before any radiological sign appears (that needs CT, blood work, or longitudinal screening). It&apos;s a decision-support tool — a second pair of eyes that doesn&apos;t get tired at 8 PM. Final diagnosis stays with you.
            </p>
          </div>
        </div>
      </section>

      {/* Modules — Chest + Extremity + Abdominal */}
      <section className="py-16 bg-stone-50 border-y border-stone-100" data-testid="modules-section">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-10">
            <Badge className="bg-amber-500 text-white mb-3">Now supports <strong className="mx-1">three</strong> X-ray types</Badge>
            <h2 className="text-2xl sm:text-3xl font-heading font-medium text-stone-900">
              One assistant. <em className="text-[#1A4D2E] not-italic">Chest, Extremity &amp; Abdominal</em> reads.
            </h2>
            <p className="text-stone-500 mt-3 max-w-2xl mx-auto text-sm sm:text-base">
              From a chesty cough to a swollen wrist after a fall to a distended abdomen — get an AI second opinion in under 10 seconds. Same login, same simple upload, three dedicated modules.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {/* Chest */}
            <Card className="border-[#1A4D2E]/20 shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="pt-6 pb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 rounded-lg bg-[#1A4D2E] flex items-center justify-center">
                    <HeartPulse className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-stone-900">Chest X-Ray</h3>
                    <p className="text-[11px] uppercase tracking-wider text-stone-400">12+ conditions</p>
                  </div>
                </div>
                <ul className="space-y-1.5 text-sm text-stone-600">
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#1A4D2E] mt-0.5 shrink-0" /> Pneumonia, TB indicators, effusion, cardiomegaly, COPD</li>
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#1A4D2E] mt-0.5 shrink-0" /> Nodules, consolidation, pneumothorax, pulmonary edema</li>
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#1A4D2E] mt-0.5 shrink-0" /> Severity score + patient-ready PDF report</li>
                </ul>
              </CardContent>
            </Card>

            {/* Extremity */}
            <Card className="border-amber-300 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden">
              <div className="absolute top-3 right-3">
                <Badge className="bg-amber-500 text-white text-[10px]">NEW</Badge>
              </div>
              <CardContent className="pt-6 pb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 rounded-lg bg-amber-500 flex items-center justify-center">
                    <Bone className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-stone-900">Extremity X-Ray</h3>
                    <p className="text-[11px] uppercase tracking-wider text-stone-400">Skeletal &amp; joints</p>
                  </div>
                </div>
                <ul className="space-y-1.5 text-sm text-stone-600">
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" /> Fractures (transverse, oblique, spiral, comminuted, greenstick, hairline)</li>
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" /> Dislocations, osteoarthritis, foreign bodies, bone lesions</li>
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" /> Wrist, ankle, forearm, shoulder, ribs, spine &amp; more</li>
                </ul>
              </CardContent>
            </Card>

            {/* Abdominal */}
            <Card className="border-blue-300 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden">
              <div className="absolute top-3 right-3">
                <Badge className="bg-amber-500 text-white text-[10px]">NEW</Badge>
              </div>
              <CardContent className="pt-6 pb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 rounded-lg bg-blue-500 flex items-center justify-center">
                    <CircleDot className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-stone-900">Abdominal X-Ray</h3>
                    <p className="text-[11px] uppercase tracking-wider text-stone-400">Acute abdomen &amp; KUB</p>
                  </div>
                </div>
                <ul className="space-y-1.5 text-sm text-stone-600">
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" /> Bowel obstruction, free intraperitoneal air, ileus</li>
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" /> Renal &amp; ureteric calculi, faecal loading, foreign bodies</li>
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" /> Urgency flag (emergency / urgent / routine)</li>
                </ul>
              </CardContent>
            </Card>
          </div>

          <p className="text-center text-xs text-stone-500 mt-6 italic">
            Perfect for GPs handling casualty walk-ins, sports injuries, paediatric falls, elderly post-fall patients and acute abdomen presentations.
          </p>
        </div>
      </section>

      {/* Comprehensive conditions detected */}
      <section className="py-14 bg-white" data-testid="conditions-detected-section">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-10">
            <p className="text-[11px] uppercase tracking-[0.25em] text-stone-400 mb-2">Detection scope</p>
            <h2 className="text-2xl sm:text-3xl font-heading font-medium text-stone-900">
              25+ conditions across three X-ray types
            </h2>
            <p className="text-stone-500 mt-3 max-w-2xl mx-auto text-sm sm:text-base">
              Every finding comes with probability %, severity band, bounding box &amp; a GP-interpretable description.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Chest */}
            <div className="rounded-xl border border-[#1A4D2E]/20 bg-[#1A4D2E]/[0.02] p-5">
              <div className="flex items-center gap-2 mb-3">
                <HeartPulse className="w-4 h-4 text-[#1A4D2E]" />
                <h3 className="text-sm font-heading font-semibold text-stone-900 uppercase tracking-wider">Chest X-Ray (12+)</h3>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {['Pneumonia', 'Tuberculosis', 'Pleural Effusion', 'Cardiomegaly', 'COPD / Emphysema', 'Atelectasis', 'Pneumothorax', 'Nodules / Masses', 'Pulmonary Edema', 'Consolidation', 'Fibrosis', 'Hilar Prominence'].map(c => (
                  <span key={c} className="text-[11px] px-2 py-1 rounded-full bg-white border border-[#1A4D2E]/20 text-[#1A4D2E]">{c}</span>
                ))}
              </div>
            </div>

            {/* Extremity */}
            <div className="rounded-xl border border-amber-300/60 bg-amber-50/40 p-5">
              <div className="flex items-center gap-2 mb-3">
                <Bone className="w-4 h-4 text-amber-700" />
                <h3 className="text-sm font-heading font-semibold text-stone-900 uppercase tracking-wider">Extremity X-Ray (10+)</h3>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {['Transverse Fracture', 'Oblique Fracture', 'Spiral Fracture', 'Comminuted Fracture', 'Greenstick', 'Hairline Fracture', 'Avulsion', 'Stress Fracture', 'Dislocation', 'Subluxation', 'Osteoarthritis', 'Osteoporosis', 'Foreign Body', 'Osteomyelitis', 'Bone Lesion', 'Old Healed Fracture'].map(c => (
                  <span key={c} className="text-[11px] px-2 py-1 rounded-full bg-white border border-amber-300/60 text-amber-800">{c}</span>
                ))}
              </div>
            </div>

            {/* Abdominal */}
            <div className="rounded-xl border border-blue-300/60 bg-blue-50/40 p-5">
              <div className="flex items-center gap-2 mb-3">
                <CircleDot className="w-4 h-4 text-blue-700" />
                <h3 className="text-sm font-heading font-semibold text-stone-900 uppercase tracking-wider">Abdominal X-Ray (8+)</h3>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {['Bowel Obstruction', 'Free Intraperitoneal Air', 'Ileus / Sentinel Loop', 'Renal Calculi', 'Ureteric Calculi', 'Faecal Loading', 'Foreign Body', 'Ascites', 'Organomegaly', 'Vascular Calcifications', 'Gallstones (radiopaque)', 'Urgency Flag'].map(c => (
                  <span key={c} className="text-[11px] px-2 py-1 rounded-full bg-white border border-blue-300/60 text-blue-800">{c}</span>
                ))}
              </div>
            </div>
          </div>

          <p className="text-center text-xs text-stone-500 mt-8 italic max-w-2xl mx-auto">
            Not a replacement for a radiologist&apos;s read — it&apos;s a second pair of eyes that never gets tired at 9 PM. Final diagnosis always stays with you.
          </p>
        </div>
      </section>

      {/* Subscribe-Now block — high-conversion inline pricing with discount visible */}
      <section className="py-16 bg-[#1A4D2E] text-white" data-testid="subscribe-now-section">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Badge className="bg-amber-500 text-white mb-4">Launch Promo · 75% OFF</Badge>
          <h2 className="text-3xl sm:text-4xl font-heading font-medium leading-tight">
            Pro plan: <s className="text-white/50 mr-2 font-mono text-2xl sm:text-3xl">₹5,500</s>
            <span className="text-amber-300 font-mono">₹1,375</span>
            <span className="text-base sm:text-lg text-white/70 font-sans"> for 6 months</span>
          </h2>
          <p className="text-white/80 mt-4 max-w-xl mx-auto text-sm sm:text-base">
            That&apos;s <strong className="text-white">₹229/month</strong> — less than a single radiologist read. Discount auto-applied. No card needed for the 2 free X-rays first.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8">
            <Button
              size="lg"
              onClick={() => navigate(registerHref)}
              className="bg-amber-500 hover:bg-amber-400 text-stone-900 text-base px-8 h-12 gap-2 rounded-xl font-semibold"
              data-testid="subscribe-now-btn"
            >
              Subscribe Now — Save 75% <ArrowRight className="w-4 h-4" />
            </Button>
            <a
              href={WHATSAPP_LINK}
              target="_blank" rel="noopener noreferrer"
              className="text-sm text-white/80 hover:text-white inline-flex items-center gap-1.5"
            >
              <MessageCircle className="w-4 h-4" /> or chat on WhatsApp
            </a>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-10 max-w-3xl mx-auto text-left">
            {[
              { icon: Check, text: 'Unlimited X-rays for 6 months' },
              { icon: Check, text: '25+ conditions detected' },
              { icon: Check, text: 'PDF reports with timestamp' },
              { icon: Check, text: 'Patient history tracking' },
            ].map((f, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <div className="w-5 h-5 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                  <f.icon className="w-3 h-3 text-amber-300" />
                </div>
                <span className="text-white/90">{f.text}</span>
              </div>
            ))}
          </div>

          <p className="text-[11px] text-white/50 mt-8">
            Code <code className="text-white/70 font-mono">{activeCoupon}</code> auto-applied · {PROMO_DEADLINE} · 100% refund within first 7 days
          </p>
        </div>
      </section>

      {/* Social proof */}
      <section className="bg-stone-50 py-10 border-y border-stone-100">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-wrap items-center justify-center gap-8 text-center">
            <div>
              <p className="text-3xl font-mono font-bold text-stone-900">10 sec</p>
              <p className="text-xs text-stone-500 mt-1">Avg. analysis time</p>
            </div>
            <div className="w-px h-10 bg-stone-200 hidden sm:block" />
            <div>
              <p className="text-3xl font-mono font-bold text-stone-900">25+</p>
              <p className="text-xs text-stone-500 mt-1">Conditions detected</p>
            </div>
            <div className="w-px h-10 bg-stone-200 hidden sm:block" />
            <div>
              <p className="text-3xl font-mono font-bold text-[#1A4D2E]">Rs 99</p>
              <p className="text-xs text-stone-500 mt-1">Per case only</p>
            </div>
            <div className="w-px h-10 bg-stone-200 hidden sm:block" />
            <div>
              <p className="text-3xl font-mono font-bold text-stone-900">24/7</p>
              <p className="text-xs text-stone-500 mt-1">Always available</p>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-heading font-medium text-stone-900 text-center">How It Works</h2>
          <p className="text-stone-500 text-center mt-2 mb-10">Three steps. Under a minute. From your phone or computer.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Upload, step: '01', title: 'Upload or Snap', desc: 'Upload a chest X-ray image or capture it directly from your phone camera. PNG, JPG supported.' },
              { icon: Brain, step: '02', title: 'AI Analyzes in Seconds', desc: 'GPT-5.2 Vision scans chest X-rays for 12+ conditions, extremity films for fractures & joint findings, and abdominal films for obstruction & free air.' },
              { icon: FileText, step: '03', title: 'Get Your Report', desc: 'Download a professional PDF with findings, severity scores, recommendations & medico-legal timestamps.' },
            ].map((s, i) => (
              <Card key={i} className="border-stone-200 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="pt-6 pb-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-[#1A4D2E]/10 flex items-center justify-center">
                      <s.icon className="w-5 h-5 text-[#1A4D2E]" />
                    </div>
                    <span className="text-xs font-mono text-stone-400">STEP {s.step}</span>
                  </div>
                  <h3 className="font-heading font-semibold text-stone-900 mb-2">{s.title}</h3>
                  <p className="text-sm text-stone-500 leading-relaxed">{s.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why doctors choose us */}
      <section className="py-16 bg-stone-50">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-heading font-medium text-stone-900 text-center">Why Busy Doctors Choose ChestBuddy</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
            {[
              { icon: Clock, title: 'Save 10 min/patient', desc: 'See 1-2 extra patients every hour. Pay for itself on day one.' },
              { icon: Shield, title: 'Medico-legal records', desc: 'Time-stamped reports with disclaimer. Proper documentation when needed.' },
              { icon: Zap, title: 'Instant second opinion', desc: 'No waiting for radiologist callback. Confident decisions at the point of care.' },
              { icon: Award, title: 'Cheaper than a lab call', desc: 'Rs 99/case or Rs 33/day unlimited. The most affordable AI assistant.' },
            ].map((f, i) => (
              <div key={i} className="p-5 rounded-xl bg-white border border-stone-200 hover:border-[#1A4D2E]/30 transition-colors">
                <f.icon className="w-6 h-6 text-[#1A4D2E] mb-3" />
                <h3 className="font-heading font-semibold text-stone-900 text-sm mb-1">{f.title}</h3>
                <p className="text-xs text-stone-500 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-heading font-medium text-stone-900 text-center">What Doctors Are Saying</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
            {[
              { name: 'Dr. Rajesh Patel', clinic: 'City Care Clinic, Pune', quote: 'I was skeptical about AI, but ChestBuddy surprised me. The reports are clear, the speed is unbelievable, and my patients trust the printed findings. Worth every rupee.' },
              { name: 'Dr. Priya Sharma', clinic: 'Sharma Diagnostics, Nagpur', quote: 'We process 30+ chest X-rays daily. ChestBuddy saves us 4-5 hours every day. The Pro plan paid for itself in the first week.' },
              { name: 'Dr. Amit Kulkarni', clinic: 'HealthFirst GP, Mumbai', quote: 'The medico-legal aspect sold me. Time-stamped AI reports give me confidence and proper documentation. No more second-guessing late-night emergency X-rays.' },
            ].map((t, i) => (
              <Card key={i} className="border-stone-200 shadow-sm">
                <CardContent className="pt-6 pb-5">
                  <div className="flex gap-1 mb-3">
                    {[1,2,3,4,5].map(s => <Star key={s} className="w-4 h-4 fill-amber-400 text-amber-400" />)}
                  </div>
                  <p className="text-sm text-stone-600 leading-relaxed mb-4">&ldquo;{t.quote}&rdquo;</p>
                  <div>
                    <p className="text-sm font-semibold text-stone-900">{t.name}</p>
                    <p className="text-xs text-stone-400">{t.clinic}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing preview */}
      <section className="py-16 bg-[#1A4D2E]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl font-heading font-medium text-white">Start Free. Scale When Ready.</h2>
          <p className="text-white/70 mt-2 mb-8">No credit card. No commitment. Just results.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white/10 rounded-xl p-5 text-white border border-white/20">
              <p className="text-xs uppercase tracking-wider text-white/60">Free Trial</p>
              <p className="text-3xl font-mono font-bold mt-2">Free</p>
              <p className="text-sm text-white/70 mt-1">2 analyses included</p>
            </div>
            <div className="bg-white rounded-xl p-5 text-stone-900 border-2 border-amber-400 relative">
              <Badge className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-amber-500 text-white text-[10px]">Popular</Badge>
              <p className="text-xs uppercase tracking-wider text-stone-500">Per Case</p>
              <p className="text-3xl font-mono font-bold mt-2">Rs 99</p>
              <p className="text-sm text-stone-500 mt-1">No subscription needed</p>
            </div>
            <div className="bg-white/10 rounded-xl p-5 text-white border border-white/20">
              <p className="text-xs uppercase tracking-wider text-white/60">Unlimited</p>
              <p className="text-3xl font-mono font-bold mt-2">Rs 33<span className="text-lg">/day</span></p>
              <p className="text-sm text-white/70 mt-1">From Rs 2,999/quarter</p>
            </div>
          </div>
          <Button size="lg" onClick={() => navigate(registerHref)} className="mt-8 bg-white text-[#1A4D2E] hover:bg-stone-100 text-base px-8 h-12 gap-2 rounded-xl font-semibold">
            Start Your Free Trial <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl font-heading font-medium text-stone-900">Ready to See It in Action?</h2>
          <p className="text-stone-500 mt-2 mb-6">Try on your own X-ray case. Takes 30 seconds to sign up.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button size="lg" onClick={() => navigate(registerHref)} className="bg-[#1A4D2E] hover:bg-[#143B23] text-white text-base px-8 h-12 gap-2 rounded-xl">
              Create Free Account <ArrowRight className="w-4 h-4" />
            </Button>
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl text-sm font-medium transition-colors">
              <MessageCircle className="w-4 h-4" /> WhatsApp Us
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-stone-950 text-white py-10">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-[#1A4D2E]" />
              <span className="font-heading font-semibold">ChestBuddy</span>
              <span className="text-xs text-stone-500">ChestAI GP-Assist</span>
            </div>
            <div className="flex items-center gap-4 text-sm text-stone-400">
              <a href="mailto:thecalltrack@gmail.com" className="hover:text-white">thecalltrack@gmail.com</a>
              <a href="tel:+919028776813" className="hover:text-white">+91 90287 76813</a>
            </div>
          </div>
          <p className="text-xs text-stone-600 text-center mt-6">AI decision support system. Does not replace professional medical judgment. All findings must be verified by a qualified physician.</p>
        </div>
      </footer>

      {/* Floating WhatsApp */}
      <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" data-testid="floating-whatsapp"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-green-500 hover:bg-green-600 rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all hover:scale-110">
        <MessageCircle className="w-7 h-7 text-white" />
      </a>
    </div>
  );
}
